import { useState, useEffect } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import { useDisableWheel } from "../hooks/use-disable-wheel";

export type NumericInputProps = {
    "id": string;
    /** The numeric value controlled by the parent. */
    "value": number;
    /** Called with the new numeric value whenever the input changes. When the field
     *  is cleared the parent receives `0`, but the input displays a blank field. */
    "onChange": (value: number) => void;
    "min"?: number;
    "max"?: number;
    "aria-label"?: string;
    /**
     * When true, treats `value` as a ratio (0–1) where 1 = 100%.
     * The input displays `value * 100` and calls `onChange` with `inputValue / 100`.
     * A `%` symbol is shown inside the field on the right.
     * `min` and `max` should still be provided as ratios (e.g. `max={1}` for 100%).
     */
    "asPercentage"?: boolean;
};

const sharedInputStyles = css({
    "width": "120px",
    "padding": `${token("space.075")} ${token("space.150")}`,
    "borderRadius": "40px",
    "border": `1px solid ${wacToken("color.neutral.light300")}`,
    "fontSize": "16px",
    "fontFamily": wacToken("font.charlie.text"),
    "appearance": "none",
    "MozAppearance": "textfield",
    "backgroundColor": wacToken("color.neutral.light100"),
    "outline": "none",
    "::placeholder": {
        color: wacToken("color.neutral.light700")
    },
    ":focus": {
        borderColor: wacToken("color.primary.blue"),
        boxShadow: `0 0 0 2px ${wacToken("color.extended.blue200")}`
    },
    "::-webkit-inner-spin-button": {
        WebkitAppearance: "none",
        margin: 0
    },
    "::-webkit-outer-spin-button": {
        WebkitAppearance: "none",
        margin: 0
    }
});

// When showing %, give the input less right padding so the symbol fits flush inside.
const percentageInputStyles = css({
    paddingInlineEnd: token("space.300"),
    boxSizing: "border-box"
});

const wrapperStyles = css({
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    width: "120px"
});

const percentSymbolStyles = css({
    position: "absolute",
    insetInlineEnd: token("space.150"),
    fontSize: "16px",
    fontFamily: wacToken("font.charlie.text"),
    color: wacToken("color.neutral.light700"),
    pointerEvents: "none",
    userSelect: "none"
});

/**
 * Presentational numeric input that:
 * - Accepts only whole numbers (no leading zeros).
 * - Shows a blank field when the user clears the input while reporting `0` to the parent.
 * - Suppresses the scroll-wheel so the page scrolls normally over the input.
 * - When `asPercentage` is true, displays ratio values (0–1) as percentages (0–100)
 *   and shows a `%` symbol inside the field on the right.
 */
export const NumericInput = (props: NumericInputProps) => {
    const {
        id,
        value,
        onChange,
        min,
        max,
        "aria-label": ariaLabel,
        asPercentage = false
    } = props;

    const toDisplayString = (v: number): string => {
        if (v === 0) return "";
        return asPercentage ? String(Math.round(v * 100)) : String(v);
    };

    const inputRef = useDisableWheel<HTMLInputElement>();

    const [displayValue, setDisplayValue] = useState<string>(
        toDisplayString(value)
    );

    // Sync display value when the parent value changes externally (e.g. a reset).
    useEffect(() => {
        setDisplayValue(toDisplayString(value));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // When asPercentage, min/max are ratios — convert to display integers for the input element.
    const displayMin =
        asPercentage && min !== undefined ? Math.round(min * 100) : min;
    const displayMax =
        asPercentage && max !== undefined ? Math.round(max * 100) : max;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;

        if (raw === "") {
            setDisplayValue("");
            onChange(0);
            return;
        }

        const parsed = parseInt(raw, 10);
        if (isNaN(parsed) || parsed < 0) {
            return;
        }

        const clamped =
            displayMin !== undefined && displayMax !== undefined
                ? Math.min(displayMax, Math.max(displayMin, parsed))
                : displayMin !== undefined
                ? Math.max(displayMin, parsed)
                : displayMax !== undefined
                ? Math.min(displayMax, parsed)
                : parsed;

        // Remove any leading zeros by converting through number and back.
        setDisplayValue(String(clamped));
        onChange(asPercentage ? clamped / 100 : clamped);
    };

    const inputEl = (
        <input
            ref={inputRef}
            id={id}
            type="number"
            css={[sharedInputStyles, asPercentage && percentageInputStyles]}
            min={displayMin}
            max={displayMax}
            value={displayValue}
            aria-label={ariaLabel}
            onChange={handleChange}
        />
    );

    if (!asPercentage) {
        return inputEl;
    }

    return (
        <div css={wrapperStyles}>
            {inputEl}
            {displayValue !== "" && (
                <span css={percentSymbolStyles} aria-hidden="true">
                    %
                </span>
            )}
        </div>
    );
};
